/* DoctorWorkspaceDeck — iter101.62
 *
 * Case-detail tab bar for the Doctor portal. Mirrors the visual
 * language of PlannerWorkspaceDeck (teal accents, icon discs,
 * hairline rules, soft depth) so the Doctor's case surface reads as
 * one cohesive command surface with the shared CommandHeaderShell.
 *
 * Unlike PlannerWorkspaceDeck (Radix `Tabs.Root` + `data-state`), the
 * Doctor's tab state is plain React `useState`, so this component is
 * a controlled primitive: `activeTab` + `onTabChange({id})` props.
 *
 * Each tab is a `DeckTrigger` — icon-disc + label + optional
 * attention affordances (pulse dot, unread ping, badge).
 */
import React from "react";


/* Individual tab pill — icon-disc + label + optional attention chip.
 * Active state driven by the `active` prop, styling matches
 * PlannerWorkspaceDeck.DeckTrigger 1:1. */
function DeckTrigger({
    id,
    icon: Icon,
    label,
    active,
    onClick,
    pulseDot = false,
    unread = false,
    badge = null,
    testid,
    title,
}) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            data-testid={testid}
            data-state={active ? "active" : "inactive"}
            title={title}
            className={[
                "group relative inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-2.5 py-1.5",
                "text-[12.5px] font-semibold text-slate-600 whitespace-nowrap",
                "transition-all duration-200 ease-out",
                "hover:bg-teal-50/60 hover:text-slate-800",
                active
                    ? "bg-gradient-to-b from-teal-50 to-teal-100/70 text-teal-900 ring-1 ring-teal-200/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_2px_10px_-4px_rgba(20,184,166,0.32)]"
                    : "",
            ].join(" ")}
        >
            {/* Icon disc — teal-tinted when idle, solid teal gradient
                with white icon when active. */}
            <span
                aria-hidden
                className={[
                    "relative w-6 h-6 rounded-full grid place-items-center shrink-0",
                    "transition-all duration-200 ease-out group-hover:scale-[1.04]",
                    active
                        ? "border-transparent bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-[inset_0_-1px_0_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.25)]"
                        : "bg-teal-50 border border-teal-200/60 text-teal-700",
                ].join(" ")}
            >
                <Icon size={13} weight="duotone" />
            </span>

            <span className="leading-tight tracking-tight">{label}</span>

            {/* Amber caps chip (used for badges like "PENDING"). */}
            {badge && (
                <span
                    className="inline-flex items-center gap-1 px-1.5 h-4 rounded-full bg-amber-100 border border-amber-200/80 text-amber-800 text-[9px] font-bold tracking-[0.14em] uppercase leading-none"
                    data-testid={`${testid}-badge`}
                >
                    <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" aria-hidden />
                    {badge}
                </span>
            )}

            {/* Inline amber pulse dot — subtle attention affordance
                without the width penalty of a full badge. */}
            {pulseDot && !badge && (
                <span
                    className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"
                    data-testid={`${testid}-dot`}
                    aria-hidden
                />
            )}

            {/* Cyan unread dot — top-right ping so it doesn't compete
                with the label width. */}
            {unread && (
                <span
                    className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-cyan-500 ring-2 ring-card"
                    data-testid={`${testid}-unread-dot`}
                    aria-hidden
                >
                    <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-70" />
                </span>
            )}
        </button>
    );
}


/**
 * <DoctorWorkspaceDeck>
 *   tabs        — array of { id, label, Icon, hint?, pulseDot?, unread?, badge? }
 *   activeTab   — id of the currently active tab
 *   onTabChange — (id) => void
 *   testIdPrefix— prefix used for each trigger's data-testid (default: "doctor-case-tab")
 *
 * Renders as a horizontal flex bar of pills. Overflows scroll on
 * narrow screens.
 */
export default function DoctorWorkspaceDeck({
    tabs,
    activeTab,
    onTabChange,
    testIdPrefix = "doctor-case-tab",
    listTestId = "doctor-case-tablist",
}) {
    return (
        <div
            role="tablist"
            data-testid={listTestId}
            className={[
                "relative bg-card border border-border/70 rounded-2xl p-1.5 h-auto",
                "flex items-center gap-1 w-full flex-nowrap overflow-x-auto scrollbar-thin",
                "shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]",
                "overflow-hidden",
            ].join(" ")}
            style={{
                backgroundImage: "linear-gradient(to right, rgba(20,184,166,0.045) 0%, transparent 40%)",
            }}
        >
            {tabs.map((t) => (
                <DeckTrigger
                    key={t.id}
                    id={t.id}
                    icon={t.Icon}
                    label={t.label}
                    active={activeTab === t.id}
                    onClick={() => onTabChange(t.id)}
                    pulseDot={!!t.pulseDot}
                    unread={!!t.unread}
                    badge={t.badge || null}
                    testid={`${testIdPrefix}-${t.id}`}
                    title={t.hint}
                />
            ))}
        </div>
    );
}
